import { Check, X } from "lucide-react";
import { CollapsibleBox } from "@/components/CollapsibleBox";
import { BOND_PHASE_KEYS, type BondPhaseState } from "@/lib/bondStateModel";
import {
  lifecycleTrackerPhases,
  type TrackerPhase,
} from "@/lib/bondLifecycleTracker";

// BRK-S414, BRK-S502 — the Bond Lifecycle box, ABOVE the submission page's
// header (the October wireframe): Bid bond → Award → Performance Bond Period →
// Maintenance → Closed, each in the state PMS published (`lifecycle`,
// PMS-S837) with PMS's note under it — the hold ("On hold · GIA Form needed")
// included. It renders; it decides nothing (#7).
//
// Each step has a bar ABOVE it: solid accent when done or current, grey when
// upcoming, dashed grey when skipped or stopped. The marker is the step's
// number, ✓ when done, × when stopped and "–" when skipped.
//
// An ordered list, so a screen reader hears the phases in order and each
// one's state in words. At 375 the five columns scroll inside their own
// wrapper rather than squeezing (BRK-S248's pattern; `relative` keeps the
// sr-only words inside the scroller so they cannot widen the page); from md
// they share the row as five columns.

/** The state in words — for a screen reader, and for anyone who cannot tell
 *  the markers apart. An unknown state is said to be unknown, never blank. */
const STATE_WORDS: Record<BondPhaseState, string> = {
  done: "complete",
  current: "in progress",
  upcoming: "not started",
  skipped: "skipped",
  stopped: "stopped",
};

const MARKER_BASE =
  "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold";

function Marker({ state, n }: { state: BondPhaseState | null; n: number }) {
  switch (state) {
    case "done":
      return (
        <span
          data-marker="done"
          aria-hidden="true"
          className={`${MARKER_BASE} bg-brk-accent/15 text-brk-accent`}
        >
          <Check size={14} strokeWidth={3} />
        </span>
      );
    case "current":
      return (
        <span
          data-marker="current"
          aria-hidden="true"
          className={`${MARKER_BASE} bg-brk-accent text-white`}
        >
          {n}
        </span>
      );
    case "skipped":
      return (
        <span
          data-marker="skipped"
          aria-hidden="true"
          className={`${MARKER_BASE} border border-dashed border-brk-dim/60 text-brk-dim`}
        >
          –
        </span>
      );
    case "stopped":
      // ×: the branch ended here (a lost bid, a withdrawal).
      return (
        <span
          data-marker="stopped"
          aria-hidden="true"
          className={`${MARKER_BASE} bg-brk-dim/20 text-brk-text`}
        >
          <X size={14} strokeWidth={3} />
        </span>
      );
    default:
      // Upcoming, and a state this build has not adopted: an outline.
      return (
        <span
          data-marker={state ?? "unknown"}
          aria-hidden="true"
          className={`${MARKER_BASE} bg-brk-panel text-brk-dim ring-1 ring-brk-border`}
        >
          {n}
        </span>
      );
  }
}

/** The bar above a step, by its state. */
function barClass(state: BondPhaseState | null): string {
  switch (state) {
    case "done":
    case "current":
      return "border-brk-accent";
    case "skipped":
    case "stopped":
      return "border-dashed border-brk-border";
    default:
      return "border-brk-border";
  }
}

function PhaseItem({ p, n }: { p: TrackerPhase; n: number }) {
  const muted = p.state === "skipped" || p.state === "upcoming";
  return (
    <li
      aria-current={p.isNow ? "step" : undefined}
      className="flex w-36 shrink-0 flex-col gap-1.5 md:w-auto md:min-w-0"
    >
      <span
        aria-hidden="true"
        data-bar
        className={`mr-2 border-t-4 ${barClass(p.state)}`}
      />
      <span className="flex flex-wrap items-center gap-1.5 pr-2">
        <Marker state={p.state} n={n} />
        <span
          data-phase-name
          className={`text-sm font-medium ${muted ? "text-brk-dim" : "text-brk-text"}`}
        >
          {p.label}
        </span>
        {p.isNow && (
          <span className="rounded-full bg-brk-accent/15 px-2 py-0.5 text-xs font-semibold uppercase text-brk-accent">
            Now
          </span>
        )}
      </span>
      <span data-state-words className="sr-only">
        {p.state === null ? "status unavailable" : STATE_WORDS[p.state]}
      </span>
      {p.note !== null && (
        <span
          className={`break-words pr-2 text-xs ${
            p.hold ? "font-medium text-brk-danger" : "text-brk-dim"
          }`}
        >
          {p.note}
        </span>
      )}
      {p.date !== null && p.dateIso !== null && (
        <time dateTime={p.dateIso} className="pr-2 text-xs text-brk-dim">
          {p.date}
        </time>
      )}
    </li>
  );
}

/**
 * The tracker in its "Bond Lifecycle" box. Every order view carries the
 * block (BRK-S500); one that is not the published shape says so in the box
 * rather than drawing an old timeline.
 */
export function BondLifecycleTracker({
  order,
}: {
  /** Read tolerantly: anything that is not the published shape reads as
   *  absent (BRK-S114). */
  order: { lifecycle?: unknown };
}) {
  const phases = lifecycleTrackerPhases(order.lifecycle);
  return (
    <CollapsibleBox title="Bond Lifecycle">
      {phases === null ? (
        <p className="text-sm text-brk-dim">
          The lifecycle for this submission isn’t available right now.
        </p>
      ) : (
        <div className="relative overflow-x-auto">
          <ol
            aria-label="Bond lifecycle"
            className="flex min-w-max gap-0 md:grid md:min-w-0 md:grid-cols-5"
          >
            {phases.map((p) => (
              <PhaseItem
                key={p.phase}
                p={p}
                // Its place in the lifecycle, not in what was served: a
                // phase PMS left out never renumbers the rest.
                n={BOND_PHASE_KEYS.indexOf(p.phase) + 1}
              />
            ))}
          </ol>
        </div>
      )}
    </CollapsibleBox>
  );
}
